// @ts-strict-ignore
import React from 'react';
import { OrgComponentProps } from '../../typescriptHelpers';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    ReleaseStepType,
    ChecklistItemTemplate,
    SubscriptionFeatureType,
    UserGroupId,
    ChecklistItemReleaseTypes,
    ChecklistItemPlacement,
    RunwayResponseType,
    AppPlatform,
} from '../../types';
import {
    selectOrgGroups,
    selectOrgLevelChecklistItemTemplates,
    selectUserAppDataByAppIdMap,
} from '../../selectors/selectors';
import ChecklistItemAdditionalInfoModal, { ChecklistItemInfo } from './../ChecklistItemAdditionalInfoModal';
import { antGrey, runwayColors } from './../styles/globalStyles';
import { PlanRestrictedFeatureWrapper } from './../planRestrictions/PlanRestrictedFeatureWrapper';
import Text from 'antd/lib/typography/Text';
import {
    displayTextForAppPlatform,
    displayTextForReleaseStepType,
    displayTextForUserChecklistGroups,
} from '../../helpers/displayHelpers';
import { PlusOutlined } from '@ant-design/icons';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import Space from 'antd/lib/space';
import Paragraph from 'antd/lib/typography/Paragraph';
import Tooltip from 'antd/lib/tooltip';
import Button from 'antd/lib/button';
import CLIEditActions from './../checklistItems/CLIEditActionsComponent';
import Table, { ColumnType } from 'antd/lib/table';
import Select from 'antd/lib/select';
import CreateUpdateChecklistItemModal from './../CreateEditChecklistItemModal';
import { globalMessage } from '@/utils/globalMessage';
import Spin from 'antd/lib/spin';
import Search from 'antd/lib/input/Search';
import { useGetEntityV2, useCallUpdatable } from '@/utils/hooks/helpers';
import { FetchableEntityTypeV2 } from '../../entityType';
import { OrgUpdatable } from '@/actions/organization';
import { DeleteOrgChecklistItemTemplateRequest } from '@/actions/stepRun';
import { allCases } from '../../typescriptHelpers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { selectUserHasEffectiveAccessToAllApps } from '@/selectors/selectors';
import { useUserCanPerformOrgAction } from '@/utils/hooks/useUserCanPerformAction';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';

interface ChecklistItemData extends ChecklistItemTemplate {
    disabled?: boolean;
}

export const OrgChecklistItemTemplates: React.FC<OrgComponentProps> = (props) => {
    const releaseSteps = [
        ReleaseStepType.Kickoff,
        ReleaseStepType.FeatureReadiness,
        ReleaseStepType.ReleaseCandidate,
        ReleaseStepType.RegressionTesting,
        ReleaseStepType.BetaTesting,
        ReleaseStepType.Metadata,
        ReleaseStepType.Screenshots,
        ReleaseStepType.Approvals,
        ReleaseStepType.Submission,
        ReleaseStepType.StoreReview,
        ReleaseStepType.Takeoff,
        ReleaseStepType.RollbackBuild,
        ReleaseStepType.Translations,
        ReleaseStepType.Rollout,
    ];
    const orgGroups = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));
    const checklistItemTemplates = useSelector((state: RootState) => selectOrgLevelChecklistItemTemplates(state));
    const [searchValue, setSearchValue] = React.useState('');
    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, props.orgId));
    const allAvailableApps = Object.values(appDataByAppIdMap);
    const allAppIds = allAvailableApps.map((appData) => appData.appId);
    const allAppPlatforms = allCases(AppPlatform);
    const hasAccessToAllApps = useSelector((state: RootState) =>
        selectUserHasEffectiveAccessToAllApps(state, props.orgId),
    );
    const canManageStepChecklistItems = useUserCanPerformOrgAction(RunwayUserAction.UAAddRemoveEditCLI);
    const canManageApprovalItems = useUserCanPerformOrgAction(RunwayUserAction.UAAddRemoveEditApprovalItems);
    const canManageRegressionItems = useUserCanPerformOrgAction(RunwayUserAction.UAAddRemoveEditRegressionItems);

    const canManageItem = (placement?: ChecklistItemPlacement) => {
        if (!hasAccessToAllApps) {
            return false;
        }

        switch (placement) {
            case ChecklistItemPlacement.Approvals:
                return canManageApprovalItems;
            case ChecklistItemPlacement.RegressionTesting:
                return canManageRegressionItems;
            case ChecklistItemPlacement.StepChecklist:
            default:
                return canManageStepChecklistItems;
        }
    };

    const [fetchChecklistItemTemplates, fetchChecklistItemTemplatesIsLoading, fetchChecklistItemTemplatesError] =
        useGetEntityV2(FetchableEntityTypeV2.OrgChecklistItemTemplates, props.orgId);

    React.useEffect(() => {
        if (checklistItemTemplates?.length === 0) {
            const data = { orgId: props.orgId };
            fetchChecklistItemTemplates(data);
        }
    }, [props.orgId]);

    React.useEffect(() => {
        if (fetchChecklistItemTemplatesError) {
            globalMessage.error('An error occurred when fetching org checklist item templates.');
        }
    }, [fetchChecklistItemTemplatesError]);

    const items = checklistItemTemplates?.map((item) => {
        return {
            ...item,
            key: item.id,
        };
    });

    const [showNewItemModal, setShowNewItemModal] = React.useState(false);
    const [showEditItemModalWithId, setShowEditItemModalWithId] = React.useState<string | null>(null);
    const [filteredReleaseStepType, setFilteredReleaseStepType] = React.useState<ReleaseStepType | null>(null);
    const [showItemDescription, setShowItemDescription] = React.useState(false);
    const [itemInfo, setItemInfo] = React.useState<ChecklistItemInfo | null>(null);

    const addNewChecklistItem = () => {
        setShowNewItemModal(true);
    };

    const filterDataSource = () => {
        var filteredItems = items;
        if (filteredReleaseStepType) {
            filteredItems = filteredItems?.filter((item) => item.releaseStepType === filteredReleaseStepType);
        }
        if (searchValue && searchValue != '') {
            filteredItems = filteredItems.filter((item) =>
                item.title.toLowerCase().includes(searchValue.toLowerCase()),
            );
        }
        return filteredItems;
    };

    const shouldDisable = (item: ChecklistItemData, showRestrictedUI: boolean) => {
        return item.disabled && showRestrictedUI;
    };

    const textColor = (item: ChecklistItemData, showRestrictedUI: boolean) => {
        return shouldDisable(item, showRestrictedUI) ? antGrey.grey6 : antGrey.grey8;
    };

    const opacity = (item: ChecklistItemData, showRestrictedUI: boolean) => {
        return shouldDisable(item, showRestrictedUI) ? '30%' : '100%';
    };

    const renderTitle = (item: ChecklistItemData) => {
        return (
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                {(isDisabled) => {
                    return (
                        <Text style={{ color: textColor(item, isDisabled) }}>
                            {displayTextForReleaseStepType(item.releaseStepType, null, null, true)}
                        </Text>
                    );
                }}
            </PlanRestrictedFeatureWrapper>
        );
    };

    const renderDescription = (item: ChecklistItemData) => {
        return (
            <Space size={0} align="center">
                <div
                    style={{
                        display: 'inline-block',
                        verticalAlign: 'middle',
                        paddingRight: 8,
                    }}
                >
                    <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                        {(isDisabled) => (
                            <Paragraph
                                ellipsis={{ rows: 2 }}
                                style={{
                                    color: textColor(item, isDisabled),
                                    marginBottom: 0,
                                    flexShrink: 1,
                                }}
                            >
                                {item.title}
                            </Paragraph>
                        )}
                    </PlanRestrictedFeatureWrapper>
                </div>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                    {(isDisabled) => (
                        <TooltipInfoIcon
                            tooltipContent={
                                shouldDisable(item, isDisabled) ? null : (
                                    <Space direction="vertical" style={{ padding: 6 }}>
                                        <Text style={{ color: 'white' }}>Identifier:</Text>
                                        <Text
                                            style={{
                                                backgroundColor: antGrey.grey5,
                                                color: runwayColors.darkTextGrey,
                                                borderRadius: 3,
                                                borderStyle: 'solid',
                                                borderColor: antGrey.grey7,
                                                borderWidth: 1,
                                                padding: '2px 4px',
                                            }}
                                        >
                                            {item.uid}
                                        </Text>
                                    </Space>
                                )
                            }
                            style={{ opacity: opacity(item, isDisabled) }}
                            hasCustomContent
                        />
                    )}
                </PlanRestrictedFeatureWrapper>
            </Space>
        );
    };

    const renderAdditionalInfoButton = (item) => {
        if (item.description) {
            return (
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                    {(isDisabled) => (
                        <Button
                            disabled={shouldDisable(item, isDisabled)}
                            onClick={() => {
                                setShowItemDescription(true);
                                setItemInfo({
                                    title: item.title,
                                    description: item.description,
                                });
                            }}
                            type="link"
                        >
                            View
                        </Button>
                    )}
                </PlanRestrictedFeatureWrapper>
            );
        } else {
            return (
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                    {(isDisabled) => <Text style={{ color: textColor(item, isDisabled) }}>-</Text>}
                </PlanRestrictedFeatureWrapper>
            );
        }
    };

    const renderActions = (item: ChecklistItemData) => {
        const userCanManageItem = canManageItem(item.placement);
        return (
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                {(isDisabled) => (
                    <CLIEditActions
                        orgId={props.orgId}
                        itemId={item.id}
                        deleteIsLoading={deleteChecklistItemOrgTemplateIsLoading}
                        onConfirmDelete={deleteItem}
                        onEditChecklistItem={(id) => setShowEditItemModalWithId(id)}
                        disableEditButton={shouldDisable(item, isDisabled) || !userCanManageItem}
                        disableDeleteButton={!userCanManageItem}
                        shouldDisableOrgLevelCliActions={false}
                    />
                )}
            </PlanRestrictedFeatureWrapper>
        );
    };

    const renderColumnTitle = (title: string) => {
        return <Text strong>{title}</Text>;
    };

    const renderApps = (item: ChecklistItemData) => {
        const apps = item.apps ?? [];
        const hasAllAppsSelected =
            item.shouldApplyToAllAppsAndPlatforms ||
            (apps.length > 0 && allAppIds.length > 0 && allAppIds.every((appId) => apps.includes(appId)));
        const appNames = apps.map((appId) => appDataByAppIdMap[appId]?.appName ?? appId);
        const shouldTruncate = appNames.length > 3;
        const truncatedText = shouldTruncate ? `${appNames.slice(0, 3).join(', ')}...` : appNames.join(', ');
        const displayAppsText =
            hasAllAppsSelected ? 'All apps'
            : appNames.length > 0 ? truncatedText
            : '-';
        return (
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                {(isDisabled) => {
                    const content = <Text style={{ color: textColor(item, isDisabled) }}>{displayAppsText}</Text>;
                    if (hasAllAppsSelected || !shouldTruncate) {
                        return content;
                    }
                    return <Tooltip title={appNames.join(', ')}>{content}</Tooltip>;
                }}
            </PlanRestrictedFeatureWrapper>
        );
    };

    const renderPlatforms = (item: ChecklistItemData) => {
        const platforms = item.platforms ?? [];
        const hasAllPlatformsSelected =
            item.shouldApplyToAllAppsAndPlatforms ||
            (platforms.length > 0 &&
                allAppPlatforms.length > 0 &&
                allAppPlatforms.every((platform) => platforms.includes(platform)));
        const platformNames = platforms.map((platform) => displayTextForAppPlatform(platform));
        const shouldTruncate = platformNames.length > 3;
        const truncatedText = shouldTruncate ? `${platformNames.slice(0, 3).join(', ')}...` : platformNames.join(', ');
        const displayText =
            hasAllPlatformsSelected ? 'All platforms'
            : platformNames.length > 0 ? truncatedText
            : '-';
        return (
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                {(isDisabled) => {
                    const content = <Text style={{ color: textColor(item, isDisabled) }}>{displayText}</Text>;
                    if (hasAllPlatformsSelected || !shouldTruncate) {
                        return content;
                    }
                    return <Tooltip title={platformNames.join(', ')}>{content}</Tooltip>;
                }}
            </PlanRestrictedFeatureWrapper>
        );
    };

    const onCancel = () => {
        setShowEditItemModalWithId(null);
        setShowNewItemModal(false);
    };

    const onSuccess = () => {
        setShowEditItemModalWithId(null);
        setShowNewItemModal(false);
    };

    const [
        deleteChecklistItemOrgTemplate,
        deleteChecklistItemOrgTemplateIsLoading,
        deleteChecklistItemOrgTemplateResponseType,
        deleteChecklistItemOrgTemplateError,
    ] = useCallUpdatable(OrgUpdatable.DeleteOrgChecklistItemTemplate, { orgId: props.orgId });

    const deleteItem = (id: string) => {
        const request: DeleteOrgChecklistItemTemplateRequest = {
            checklistItemId: id,
        };
        deleteChecklistItemOrgTemplate(request);
    };

    React.useEffect(() => {
        if (deleteChecklistItemOrgTemplateError) {
            globalMessage.error(deleteChecklistItemOrgTemplateError);
        }
    }, [deleteChecklistItemOrgTemplateError]);

    React.useEffect(() => {
        if (deleteChecklistItemOrgTemplateResponseType === RunwayResponseType.Success) {
            globalMessage.success('Checklist item template deleted!');
        }
    }, [deleteChecklistItemOrgTemplateResponseType]);

    const columns: ColumnType<ChecklistItemTemplate>[] = [
        {
            title: renderColumnTitle('Release step'),
            render: renderTitle,
        },
        {
            title: renderColumnTitle('Checklist item'),
            render: renderDescription,
        },
        {
            title: renderColumnTitle('Additional info'),
            align: 'center',
            render: renderAdditionalInfoButton,
        },
        {
            title: renderColumnTitle('Owners'),
            dataIndex: 'approverGroups',
            align: 'center',
            render: (approverGroups: UserGroupId[], cli) => {
                if (!approverGroups) {
                    return null;
                }
                if (approverGroups.length > 0) {
                    const groups = approverGroups.map((groupId) => orgGroups[groupId]);
                    return (
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                            {(isDisabled) => (
                                <Text style={{ color: textColor(cli, isDisabled) }}>
                                    {displayTextForUserChecklistGroups(groups)}
                                </Text>
                            )}
                        </PlanRestrictedFeatureWrapper>
                    );
                } else {
                    return (
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                            {(isDisabled) => <Text style={{ color: textColor(cli, isDisabled) }}>No owners</Text>}
                        </PlanRestrictedFeatureWrapper>
                    );
                }
            },
        },
        {
            title: renderColumnTitle('Release types'),
            dataIndex: 'releaseTypes',
            align: 'center',
            render: (releaseTypes: ChecklistItemReleaseTypes, cli) => {
                const renderReleaseTypeText = (text: string) => {
                    return (
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                            {(isDisabled) => <Text style={{ color: textColor(cli, isDisabled) }}>{text}</Text>}
                        </PlanRestrictedFeatureWrapper>
                    );
                };
                switch (releaseTypes) {
                    case ChecklistItemReleaseTypes.All:
                        return renderReleaseTypeText('All types');
                    case ChecklistItemReleaseTypes.AllExceptHotfixAndRollback:
                        return renderReleaseTypeText('All types except hotfixes');
                    case ChecklistItemReleaseTypes.HotfixOnly:
                        return renderReleaseTypeText('Hotfixes only');
                    case ChecklistItemReleaseTypes.RollbackOnly:
                        return renderReleaseTypeText('Rollbacks only');
                }
            },
        },
        {
            title: renderColumnTitle('Apps'),
            align: 'center',
            render: renderApps,
        },
        {
            title: renderColumnTitle('Platforms'),
            align: 'center',
            render: renderPlatforms,
        },
        {
            title: '',
            align: 'right',
            render: renderActions,
        },
    ];

    if (fetchChecklistItemTemplatesIsLoading || !checklistItemTemplates) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100vh',
                    flexGrow: 1,
                }}
            >
                <Spin />
            </div>
        );
    }

    return (
        <div>
            <div>
                <Text style={{ marginTop: 15 }}>
                    Manage checklist items that will populate to multiple workflows in your organization.
                </Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginTop: 30 }}>
                        <Select
                            placeholder="Filter by release step"
                            onChange={(value) => setFilteredReleaseStepType(value as ReleaseStepType)}
                            style={{ minWidth: 200 }}
                        >
                            <Select.Option key={0} value={null}>
                                All release steps
                            </Select.Option>
                            {releaseSteps.map((releaseStepType, index) => {
                                return (
                                    <Select.Option key={index + 1} value={releaseStepType}>
                                        {displayTextForReleaseStepType(releaseStepType, null, null, true)}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                        <Search
                            placeholder="Search"
                            allowClear
                            style={{ width: 443, marginLeft: 10 }}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onSearch={(value) => {
                                setSearchValue(value);
                            }}
                        />
                    </div>
                </div>
                <Space>
                    <Tooltip
                        title={
                            canManageItem(ChecklistItemPlacement.StepChecklist) ? undefined
                            : !hasAccessToAllApps ?
                                'You must have access to every app in the organization to manage organization-level checklist items.'
                            :   DISALLOWED_ACTION_ERROR_MESSAGE
                        }
                    >
                        <span>
                            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI} requiresTooltip>
                                <Button
                                    style={{ float: 'right' }}
                                    icon={<PlusOutlined />}
                                    onClick={addNewChecklistItem}
                                    disabled={!canManageItem(ChecklistItemPlacement.StepChecklist)}
                                >
                                    Add
                                </Button>
                            </PlanRestrictedFeatureWrapper>
                        </span>
                    </Tooltip>
                </Space>
            </div>

            <Table<ChecklistItemTemplate>
                style={{ marginTop: 24, marginBottom: 12 }}
                size="middle"
                locale={{
                    emptyText: 'No checklist items added yet',
                }}
                columns={columns}
                dataSource={filterDataSource()}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
            />
            <CreateUpdateChecklistItemModal
                orgId={props.orgId}
                open={showNewItemModal || showEditItemModalWithId !== null}
                isEditingItemWithId={showEditItemModalWithId}
                onCancel={onCancel}
                onSuccess={onSuccess}
                releaseStepTypes={releaseSteps}
                placement={ChecklistItemPlacement.StepChecklist}
                stepRunComponentProps={null}
                releaseStepType={filteredReleaseStepType}
                isOrgLevelTemplate={true}
            />
            <ChecklistItemAdditionalInfoModal
                title={itemInfo?.title ?? ''}
                description={itemInfo?.description ?? ''}
                isVisible={!!showItemDescription}
                onCancel={() => setShowItemDescription(false)}
                afterClose={() => setItemInfo(null)}
            />
        </div>
    );
};
